import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      title: 'Política de Privacidad',
      lastUpdated: 'Última actualización: Enero 2026',
      backHome: 'Volver al inicio',
      sections: [
        {
          title: '1. Información que Recopilamos',
          content: `En Domotics, recopilamos información que usted nos proporciona directamente cuando:
          
• Completa nuestro formulario de contacto (nombre, email, teléfono, mensaje)
• Se comunica con nosotros por correo electrónico o teléfono
• Solicita información sobre nuestros servicios

No recopilamos información automáticamente a través de cookies de seguimiento ni compartimos sus datos con terceros para fines publicitarios.`
        },
        {
          title: '2. Uso de la Información',
          content: `Utilizamos la información recopilada para:

• Responder a sus consultas y solicitudes de información
• Proporcionarle presupuestos y propuestas de servicios
• Mantener comunicación relacionada con proyectos contratados
• Mejorar nuestros servicios y atención al cliente
• Cumplir con obligaciones legales aplicables`
        },
        {
          title: '3. Protección de Datos',
          content: `Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger sus datos personales contra:

• Acceso no autorizado
• Alteración o divulgación indebida
• Destrucción accidental o ilícita

Sus datos se almacenan en servidores seguros con acceso restringido únicamente al personal autorizado.`
        },
        {
          title: '4. Conservación de Datos',
          content: `Conservamos sus datos personales únicamente durante el tiempo necesario para cumplir con los fines para los que fueron recopilados, incluyendo:

• Datos de contacto: hasta 2 años desde la última comunicación
• Datos de clientes: según los plazos legales establecidos para documentación comercial y fiscal

Puede solicitar la eliminación de sus datos en cualquier momento.`
        },
        {
          title: '5. Sus Derechos',
          content: `De acuerdo con el Reglamento General de Protección de Datos (RGPD), usted tiene derecho a:

• Acceder a sus datos personales
• Rectificar datos inexactos
• Solicitar la eliminación de sus datos
• Oponerse al tratamiento de sus datos
• Solicitar la portabilidad de sus datos
• Retirar su consentimiento en cualquier momento

Para ejercer estos derechos, contáctenos en: admin@domodomotics.com`
        },
        {
          title: '6. Responsable del Tratamiento',
          content: `Domotics
Domicilio: España
Email: admin@domodomotics.com
Web: www.domodomotics.es`
        },
        {
          title: '7. Cambios en esta Política',
          content: `Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en este sitio web. Le recomendamos revisar esta página periódicamente.`
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: January 2026',
      backHome: 'Back to home',
      sections: [
        {
          title: '1. Information We Collect',
          content: `At Domotics, we collect information that you provide directly when you:
          
• Complete our contact form (name, email, phone, message)
• Communicate with us by email or phone
• Request information about our services

We do not automatically collect information through tracking cookies nor share your data with third parties for advertising purposes.`
        },
        {
          title: '2. Use of Information',
          content: `We use the information collected to:

• Respond to your inquiries and information requests
• Provide you with quotes and service proposals
• Maintain communication related to contracted projects
• Improve our services and customer support
• Comply with applicable legal obligations`
        },
        {
          title: '3. Data Protection',
          content: `We implement appropriate technical and organizational security measures to protect your personal data against:

• Unauthorized access
• Improper alteration or disclosure
• Accidental or unlawful destruction

Your data is stored on secure servers with access restricted only to authorized personnel.`
        },
        {
          title: '4. Data Retention',
          content: `We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including:

• Contact data: up to 2 years from the last communication
• Customer data: according to legal deadlines established for commercial and tax documentation

You can request the deletion of your data at any time.`
        },
        {
          title: '5. Your Rights',
          content: `In accordance with the General Data Protection Regulation (GDPR), you have the right to:

• Access your personal data
• Rectify inaccurate data
• Request the deletion of your data
• Object to the processing of your data
• Request data portability
• Withdraw your consent at any time

To exercise these rights, contact us at: admin@domodomotics.com`
        },
        {
          title: '6. Data Controller',
          content: `Domotics
Address: Spain
Email: admin@domodomotics.com
Web: www.domodomotics.es`
        },
        {
          title: '7. Changes to this Policy',
          content: `We reserve the right to update this privacy policy at any time. Changes will take effect immediately after publication on this website. We recommend reviewing this page periodically.`
        }
      ]
    }
  };

  const t = content[language] || content.es;

  return (
    <div className="min-h-screen bg-obsidian pt-32 pb-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-brand-light hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={20} />
          {t.backHome}
        </Link>

        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">{t.title}</h1>
          <p className="text-white/40">{t.lastUpdated}</p>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {t.sections.map((section, index) => (
            <div key={index} className="glass-card p-8 rounded-2xl">
              <h2 className="font-heading text-xl font-medium text-white mb-4">{section.title}</h2>
              <p className="text-white/55 leading-relaxed whitespace-pre-line">{section.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
